import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { Province } from '@vw-marketing/vw-ca-types';
import { getProvinceFromPostalCode } from '@vw-marketing/vwca-utils';
import React, { Dispatch, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { formatPostalCode, validatePostalCode } from './utils';
import { LayerContext, useFocusLayer } from '../Layers';
import UserLocationLayer from './UserLocationLayer';
import { LocationContext } from './LocationContext';
import { USER_LOCATION_LOCAL_STORAGE } from './utils/local-storage';

interface UserLocationContext {
  postalChecked?: boolean;
  postalCode?: string;
  province?: Province;
  distance: number;
  setPostalCode?: (postalCode: string) => void;
  setDistance?: Dispatch<React.SetStateAction<number>>;
  getLocationParams?: () => string;
  parseLocationParams?: () => void;
  promptUserForLocation?: (postalCode: string) => void;
  userCloseable: boolean;
}

export const UserLocationContext = React.createContext<UserLocationContext>({distance: 100, userCloseable: false});

export const UserLocationContextProvider = ({ children }: { children?: ReactNode }) => {
  const locationCtx = useContext(LocationContext);
  const [ userCloseable, setUserCloseable ] = useState(false);
  const [ postalChecked, setPostalChecked ] = useState(false);
  const [ postalCode, setPostalCode ] = useState<string | undefined>("");
  const [ province, setProvince ] = useState<Province | undefined>();
  const [ distance, setDistance ] = useState(100);

  const { closeLayer } = useContext(LayerContext);

  const openCustomULFLayer = useFocusLayer('custom-user-location-manager-layer', {
    size: FocusLayerSizeV2.A,
    onClose: () => closeLayer('custom-user-location-manager-layer'),
    userCloseable
  });

  const promptUserForLocation = (postalCode = "") => {
    openCustomULFLayer((close) => <UserLocationLayer postalCode={postalCode} close={close}/>)
  };

  const getLocationParams = () => {
    const queries = {
      postal_code: postalCode,
      distance: distance
    };

    return new URLSearchParams(queries as unknown as URLSearchParams).toString();
  };

  const parseLocationParams = () => {
    const params = new URLSearchParams(window.location.search);

    if (params.has('postal_code')) {
      const urlPostalCode = params.get('postal_code') as string;
      const formattedPostalCode = formatPostalCode(urlPostalCode, true);
      if (validatePostalCode(formattedPostalCode)) {
        locationCtx?.set(formattedPostalCode);
      }
    }

    if (params.has('distance')) {
      const distance = Number(params.get('distance'));
      setDistance(distance);
    }
  };

  useEffect(() => {

    if(locationCtx?.value) {
      setPostalCode(locationCtx.value);
      setPostalChecked(true);
    }
    else {
      if (!localStorage.getItem(USER_LOCATION_LOCAL_STORAGE)) {
        setPostalChecked(true);
      }
    }

  }, [locationCtx?.value]);

  useEffect(() => {
    if (postalCode?.length) {
      const prov = getProvinceFromPostalCode(postalCode);
      setProvince(prov);
    }
  }, [postalCode]);

  useEffect(() => {
    if (postalChecked && postalCode?.length && !userCloseable) {
      setUserCloseable(true);
    }
  }, [userCloseable, postalChecked, postalCode]);

  const UserLocationContextValue: UserLocationContext = useMemo(() => ({
    postalChecked,
    userCloseable,
    postalCode,
    province,
    distance,
    setPostalCode,
    setDistance,
    getLocationParams,
    parseLocationParams,
    promptUserForLocation,
  }), [postalChecked, userCloseable, postalCode, province, distance]);

  return (
    <UserLocationContext.Provider value={UserLocationContextValue}>
      { children }
    </UserLocationContext.Provider>
  );
};
